<template lang="pug">
	.audio-tracker-dynamic-eq.flex-col.full-width.space-large.center.align-center
		.flex-col.space
			.flex-row.full-width.space
				.flex-col.space.align-center
					.font-tiny.upper.grey {{$tCaption('Pre')}}
					AudioTrackerVUMeter(ref="VUMeter", orientation="vertical", :tickVisible="false", size="220px", thickness="12px", :isPlaying="tracker.isPlaying", :filters="{ stereoAnalyser: instance && instance.getPreAnalyser() }")
				.flex-1
					.flex-col.space
						.flex-row.bands
							.band.flex-col.flex-1(v-for="(band, bandIndex) in values", :class="{ active: band.gain !== 0 || band.threshold !== 0 }", :key="`band${bandIndex}`")
								.flex-row.band-value.align-center.center.space
									.flex-1.text-end.name.full-height.align-center.flex-row.justify-end {{$tCaption('Frequency')}}
									.value
										DxNumberBox.small(:value.sync="band.frequency", :width="inputWidth")
								.flex-row.band-value.align-center.center.space
									.flex-1.text-end.name.full-height.align-center.flex-row.justify-end
										.flex-col 
											.upper {{$tCaption('Gain')}}
											PopupNumberEdit(:value="band.gain", color="#55ff72", unit="dB", :showSign="true", :def="0", @changed="v => onGainChanged(band, v)")
									.value.flex-row.align-center.center
										RoundButton(ref="gainBtn", :width="45", :value="band.gain", @change="v => onGainChanged(band, v)", color="#55ff72", :step="0.1", :round="1", unit="dB"
											:max="30", :min="-30", :showValue="false", hintValueType="value", :forceScales="true", scaleColor="#ffffff", :scaleDegrees="[-30, 210]", :default="defs['gain']")
								.flex-row.band-value.align-center.center.space
									.flex-1.text-end.name.full-height.align-center.flex-row.justify-end 
										.flex-col 
											.upper {{$tCaption('Threshold')}}
											PopupNumberEdit(:value.sync="band.threshold", color="#f27649", unit="dB", :title="$tCaption('Threshold')", :showSign="true", :def="0")
									.value.flex-row.align-center.center
										RoundButton(ref="thresholdBtn", :width="45", :value="band.threshold", @change="v => band.threshold = v", color="#f27649", :step="0.5", :round="1", unit="dB"
											:max="0", :min="-60", :showValue="false", hintValueType="value", :forceScales="true", scaleColor="#ffffff", :scaleDegrees="[-30, 180]", :default="defs['threshold']")
								.flex-row.band-value.align-center.center.space
									.flex-1.text-end.name.full-height.align-center.flex-row.justify-end 
										.flex-col 
											.upper {{$tCaption('Attack')}}
											PopupNumberEdit(:value.sync="band.attack", color="#55ffff", unit="s", :title="$tCaption('Attack')", :precision="0.001", :def="0.005")
									.value.flex-row.align-center.center
										RoundButton(:width="35", :value="band.attack", @change="v => band.attack = v", color="#55ffff", :step="0.001", :round="3", unit="(sec)"
											:max="2", :min="0", :showValue="false", hintValueType="value", :forceScales="true", scaleColor="#ffffff", :scaleDegrees="[-30, 210]", :stepSensivity="0.5", :default="defs['attack']")
								.flex-row.band-value.align-center.center.space
									.flex-1.text-end.name.full-height.align-center.flex-row.justify-end 
										.flex-col 
											.upper {{$tCaption('Release')}}
											PopupNumberEdit(:value.sync="band.release", color="#d555ff", unit="s", :title="$tCaption('Release')", :precision="0.001", :def="0.16")
									.value.flex-row.align-center.center
										RoundButton(:width="35", :value="band.release", @change="v => band.release = v", color="#d555ff", :step="0.001", :round="3", unit="(sec)"
											:max="2", :min="0", :showValue="false", hintValueType="value", :forceScales="true", scaleColor="#ffffff", :scaleDegrees="[-30, 210]", :stepSensivity="0.5", :default="defs['release']")
								.flex-row.band-value.align-center.center.space
									.flex-1.text-end.name.full-height.align-center.flex-row.justify-end 
										.flex-col 
											.upper {{$tCaption('Ratio')}}
											PopupNumberEdit(:value.sync="band.ratio", color="#d1c73c", unit=":1", :title="$tCaption('Ratio')", :precision="0.001", :def="2")
									.value.flex-row.align-center.center
										RoundButton(:width="35", :value="band.ratio", @change="v => band.ratio = v", color="#d1c73c", :step="0.1", :round="1",
											:max="10", :min="0", :showValue="false", hintValueType="value", :forceScales="true", scaleColor="#ffffff", :scaleDegrees="[-30, 210]", :stepSensivity="3", :default="defs['ratio']")
								.flex-row.band-value.align-center.center.space
									.flex-1.text-end.name.full-height.align-center.flex-row.justify-end
										.flex-row.align-center.justify
											.flex-row.space-small
												span
												.switch.upper(:style="{ '--color': '#c7c7c8', '--font-size': '6px', '--border-size': '1.5px' }" :class="{ 'active': !band.off }", @click="$set(band, 'off', !band.off)") {{!band.off ? $tCaption('ON') : $tCaption('OFF')}}
											span {{$tCaption('Q')}}
									.value
										DxNumberBox.small(:value.sync="band.q", :width="inputWidth")
				.flex-col.space.align-center
					.font-tiny.upper.grey {{$tCaption('Post')}}
					AudioTrackerVUMeter(ref="VUMeter", orientation="vertical", :tickVisible="false", size="220px", thickness="12px", :isPlaying="tracker.isPlaying", :filters="{ stereoAnalyser: instance && instance.getPostAnalyser() }")
			DynamicEqChart(:series="chartSeries", :width="960", :height="290", @changing="onChartChange", @change="onChartChange", :yAxis="yAxis")
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import AudioTrackerButton from '../AudioTrackerButton.vue';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxNumberBox from 'devextreme-vue/number-box';
	import AudioTrackerVUMeter from '../AudioTrackerVUMeter.vue';
	import audioHelper from 'view/audioTracker/audio-helper';
	import DynamicEqChart from './DynamicEqChart.vue';
	import RoundButton from '../AudioTrackerRoundButton.vue';
	import config from 'app-core/config';
	import PopupNumberEdit from './PopupNumberEdit.vue';

	export default {
		translationCategory: 'audioTracker.AudioTrackerDynamicEq',
		name: 'audio-tracker-dynamic-eq',
		components: { DxSelectBox, DxNumberBox, AudioTrackerButton, AudioTrackerVUMeter, DynamicEqChart, RoundButton, PopupNumberEdit },
		props: {
			playing: VueTypes.bool.def(false),
			tracker: VueTypes.any.def(null),
			instance: VueTypes.any.def(null)
		},
		data() {
			return {
				types: [
					{ id: 0, text: this.$tCaption('Peaking'), icon: 'ff-peaking-filter' },
					{ id: 1, text: this.$tCaption('Low-pass'), icon: 'ff-low-pass-filter'  },
					{ id: 2, text: this.$tCaption('High-pass'), icon: 'ff-high-pass-filter'  },
					{ id: 3, text: this.$tCaption('Band-pass'), icon: 'ff-band-pass-filter'  },
					{ id: 4, text: this.$tCaption('Low-shelf'), icon: 'ff-low-shelf-filter'  },
					{ id: 5, text: this.$tCaption('High-shelf'), icon: 'ff-high-shelf-filter'  },
					{ id: 6, text: this.$tCaption('Notch'), icon: 'ff-notch-filter'  },
					{ id: 7, text: this.$tCaption('All-pass'), icon: 'ff-all-pass-filter'  },
				],
				inputWidth: 50,
				actualSerie: null,
				spectrumSerie: null,
				yAxis: {
					labels: Array.from({ length: 10 }, (_, i) => ({
						value: -60 + i * 10
					})),
					min: -60,
					max: 30,
					label: this.$tCaption('Threshold (dB)')
				},
				defs: {}
			};
		},
		computed: {
			values: {
				get: function() {
					return this.instance?.values;
				},
				set: function(v) {
					this.$nextTick(() => {
						this.instance.setValues(v)
					});
				}
			},
			thresholdSerie() {
				return {
					name: "eqThreshold",
					showPointLabels: true,
					pointLabelColor: "#fff",
					type: "spline",
					points: this.values.map(f => ({
						frequency: f.frequency,
						dB: f.threshold,
						color: f.threshold === 0 ? '#f2764966' : null,
						label: f.threshold === 0 ? ' ' : null,
					})),
					lineColor: "#f27649",
					lineWidth: 2,
					fillColor: "#f27649",
					pointColor: "#f27649",
					editable: true
				}
			},
			gainSerie() {
				return {
					name: "eqGain",
					showPointLabels: false,
					pointLabelColor: "#fff",
					type: "spline-area",
					points: this.values.map(f => ({
						frequency: f.frequency,
						dB: f.gain,
						color: f.gain === 0 ? '#f2764966' : null,
						label: f.gain === 0 ? ' ' : null,
					})),
					lineColor: "#55ff72",
					lineWidth: 2,
					fillColor: "#55ff72",
					pointColor: "#f27649",
					editable: true,
					hidePoints: true,
					baseLineSerie: 'actual',
					opacity: 0.2,
				}
			},
			chartSeries() {
				return [
					...(this.spectrumSerie ? [this.spectrumSerie] : []),
					this.thresholdSerie,
					this.gainSerie,
					...(this.actualSerie ? [this.actualSerie] : [])
				]
			}
		},
		watch: {
			values: {
				handler() {
					this.$emit('change', this.values);
					this.$applyValues();
				},
				deep: true
			},
			'tracker.isPlaying'() {
				this.$nextTick(() => {
					this.clearActualValues();
					this.$nextTick(() => {
						this.instance.activateUI();
					});
				});
			},
			'instance'() {
			}
		},
		mounted() {
			if (__DEBUG__)
				window.dynamicEqWapi = this;
			this.$applyValues = _.debounce(this.applyValues, 300, {
				maxWait: 1000,
				leading: false,
				trailing: true
			});

			this.$eventsBus.$on('dynamic-eq--playing-delta-change', this.onPlayingDeltaChange);

			this.actualSerie = {
				name: "actual",
				showPointLabels: false,
				hidePoints: true,
				pointLabelColor: "#fff",
				type: "spline",
				points: [
					...this.values.map((f, i) => ({
						frequency: f.frequency,
						dB: f.gain
					}))
				],
				lineColor: "#55ff72",
				lineWidth: 2,
				fillColor: "#55ff72",
				pointColor: "#55ff72",
				opacity: 0.8,
				editable: false
			};

			this.spectrumSerie = {
				name: "spectrum",
				showPointLabels: false,
				hidePoints: true,
				pointLabelColor: "#fff",
				type: "spline-area",
				points: [],
				lineColor: "#dedede",
				lineWidth: 2,
				fillColor: "#dedede",
				pointColor: "#dedede",
				opacity: 0.2,
				editable: false,
				zeroDb: -60
			};

			if (this.instance?.getDefaultValues) {
				this.$set(this, 'defs', this.instance.getDefaultValues()[0]);
			}
		},
		destroyed() {
			this.$eventsBus.$off('dynamic-eq--playing-delta-change', this.onPlayingDeltaChange);
		},
		wapiInstance(opts) {
			if (__DEBUG__)
				console.debug('Instantiate equalizer');

			const values = [];
			let inputGainNode = null;
			let outputGainNode = null;
			let preAnalyserNode = null;
			let postAnalyserNode = null;

			let audioContext = null;
			let dynEqNode = null;

			const applyDynamicValues = () => {
				if (dynEqNode) {
					dynEqNode.port.postMessage({
						type: 'updateBands',
						bands: values
					});
				}
			}

			let getDefaults = () => [60, 200, 600, 2000, 6000, 10000].map(f => ({
				frequency: f,
				gain: 0,
				q: 1,
				threshold: 0,
				ratio: 2,
				attack: 0.005,
				release: 0.16,
				off: false
			}));

			return {
				getPreAnalyser() {
					return preAnalyserNode;
				},
				getPostAnalyser() {
					return postAnalyserNode;
				},
				values,
				component: () => opts.component,
				getValues: () => values,
				getDefaultValues: getDefaults,
				clearValues: () => {
					if (__DEBUG__)
						console.debug('clearValues');
					values.splice(0, 999);
					values.push(...getDefaults());
					applyDynamicValues();
				},
				setValues: (v) => {
					let isInit = !values.length;
					if (isInit) {
						values.push(...getDefaults());
					}

					if (v.length) {
						let hasChanges = false;
						v.map((f, i) => {
							Object.keys(f).map(k => {
								if (values[i][k] !== f[k]) {
									values[i][k] = f[k];
									hasChanges = true;
								}
							});
						});
						
						if (hasChanges) {
							applyDynamicValues();
						}
					} else {
						if (isInit) {
							applyDynamicValues();
						}
					}
				},
				getUI: () => ({
					width: 1020,
					height: 700
				}),
				getAudioPipeline: async(opts) => {
					audioContext = opts?.audioContext || new AudioContext();

					const path = window.location.pathname.split('/');
					const workletDir = path.slice(0, path.length - 1).join('/');
					await audioContext.audioWorklet.addModule(`${workletDir}/dynamicEqWorklet.js`);
					dynEqNode = new AudioWorkletNode(audioContext, 'dynamic-equalizer-processor');

					if (__DEBUG__)
						console.debug('DynamicEQ', { dynEqNode });

					inputGainNode = audioContext.createGain();
					outputGainNode = audioContext.createGain();

					preAnalyserNode = audioHelper.StereoAnalyserNode(audioContext);
					preAnalyserNode.fftSize = 512;

					postAnalyserNode = audioHelper.StereoAnalyserNode(audioContext);
					postAnalyserNode.fftSize = 512;

					applyDynamicValues();

					dynEqNode.port.onmessage = (event) => {
						if (event.data.type === 'delta') {
							config.getVueApp()?.$eventsBus.$emit('dynamic-eq--playing-delta-change', event.data.value);
						}

						if (event.data.type === 'spectrum') {
							config.getVueApp()?.$eventsBus.$emit('dynamic-eq--playing-delta-change', void 0, event.data.value);
						}

						if (event.data.type === 'silent') {
							config.getVueApp()?.$eventsBus.$emit('dynamic-eq--playing-delta-change', values.map(() => 0), 0);
						}
					};

					inputGainNode.connect(preAnalyserNode);
					inputGainNode.connect(dynEqNode);
					dynEqNode.connect(outputGainNode);
					outputGainNode.connect(postAnalyserNode);

					return {
						input: inputGainNode,
						output: outputGainNode
					}
				},
				setPlayState(isPlaying) {
				},
				applyDynamicValues,
				dynEqNode,
				activateUI() {
					dynEqNode.port.postMessage({
						type: 'activateUI'
					});
				}
			}
		},
		methods: {
			clearValues() {
				this.actualSerie.points.map((f, i) => f.dB = this.values[i].gain);
			},
			clearActualValues() {
				this.values.map((f, i) => this.actualSerie.points[i].dB = this.gainSerie.points[i].dB);
				this.$refs.thresholdBtn.map(f => f.updateLevel([-60, -60]));
				this.$refs.gainBtn.map(f => f.updateLevel([-60, -60]));
				this.spectrumSerie.points = [];
			},
			onPlayingDeltaChange(deltaThresholds, spectrum) {
				if (this.tracker?.isPlaying) {
					if (spectrum) {
						this.spectrumSerie.points = spectrum.filter(f => f[0] >= 80 && f[0] <= 16000).map(f => ({ frequency: f[0], dB: f[1] }));
					}

					if (spectrum === 0) {
						this.spectrumSerie.points = [];
					}

					if (deltaThresholds) {
						deltaThresholds.map((f, i) => {
							let band = this.values[i];
							let deltaCompression = f.level > band.threshold ? f.deltaCompression : 0;

							this.actualSerie.points[i].dB = this.gainSerie.points[i].dB + deltaCompression;
							if (!band.off) {
								this.$refs.thresholdBtn[i]?.updateLevel([-60, f.level]);
								
								if (deltaCompression < 0) {
									this.$refs.gainBtn[i]?.updateLevel([band.gain + deltaCompression, band.gain]);
								} else {
									this.$refs.gainBtn[i]?.updateLevel([band.gain, band.gain + deltaCompression]);
								}
							} else {
								this.$refs.thresholdBtn[i]?.updateLevel([-60, -60]);
								this.$refs.gainBtn[i]?.updateLevel([-60, -60]);
							}
						});
					}
				}
			},
			onChartChange(opts) {
				this.values[opts.pointIndex].frequency = opts.frequency;
				this.values[opts.pointIndex].threshold = opts.dB;
				this.actualSerie.points[opts.pointIndex].frequency = opts.frequency;
			},
			applyValues() {
				this.instance.applyDynamicValues();
			},
			onGainChanged(band, v) {
				band.gain = v;
				let bandIndex = this.values.indexOf(band);
				this.actualSerie.points[bandIndex].dB = v;
			}
		}
	};
</script>
<style lang='scss'>
	.audio-tracker-dynamic-eq {
		@import "main/themes/generated/variables.base.scss";

		.eq-holder {
			width: 300px;
			max-width: 300px;

			.eq-text {
				text-align: center;
			}
		}

		.eq-range-editor {
			height: 250px;
		}

		.bands {
			.band {
				margin: 1px;
				background: #5b5b63;
				border-radius: 4px;
				overflow: hidden;
				border: 2px solid #5b5b63;
				
				.band-value {
					margin-bottom: -2px;

					.name {
						padding-right: 6px;
						font-size: 10px;
						background: #646469;
					}

					&:nth-child(even) {
						background: #5b5b63;
					}

					> .value {
						min-width: 60px;
					}
				}

				&.active {
					border: 2px solid $base-accent;
				}
			}
		}

	}
</style>
